import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronUp,
  ChevronDown,
  X,
  Radio,
  Building2,
  Calendar,
  Activity
} from 'lucide-react';

const RAGStatusList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedRAGStatus, setSelectedRAGStatus] = useState('');
  const [selectedTrend, setSelectedTrend] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  
  // Sort states
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    total: 0,
    red: 0,
    amber: 0,
    green: 0,
    trendingUp: 0,
    trendingDown: 0,
    stable: 0
  });

  useEffect(() => {
    // Use mock data for now
    loadMockData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm, selectedProject, selectedType, selectedRAGStatus, selectedTrend]);

  useEffect(() => {
    calculateSummary();
  }, [filteredData]);

  const loadMockData = () => {
    // Mock projects
    const mockProjects = [
      { id: 1, projectName: 'Telkomsel Jakarta Selatan Tower Project', projectCode: 'TSEL-JKT-2025-001' },
      { id: 2, projectName: 'IOH Fiber Optic Jakarta-Bandung', projectCode: 'IOH-FO-2025-001' },
      { id: 3, projectName: 'XL Smart Tower Expansion Surabaya', projectCode: 'XLS-TWR-2025-001' },
      { id: 4, projectName: 'BAKTI Palapa Ring Segment', projectCode: 'BAKTI-FO-2025-001' }
    ];
    setProjects(mockProjects);

    // Mock data
    const mockData = [
      {
        id: 1,
        type: 'TOWER',
        typeIcon: Building2,
        name: 'JKT-South-001',
        code: 'TWR-JKT-001',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        location: 'Jakarta Selatan',
        progress: 85,
        ragStatus: 'GREEN',
        trend: 'UP',
        lastUpdate: '2025-01-18T10:30:00',
        budget: 500000000,
        realization: 425000000,
        variance: -75000000,
        issues: 0
      },
      {
        id: 2,
        type: 'TOWER',
        typeIcon: Building2,
        name: 'JKT-South-002',
        code: 'TWR-JKT-002',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        location: 'Jakarta Selatan',
        progress: 45,
        ragStatus: 'AMBER',
        trend: 'STABLE',
        lastUpdate: '2025-01-17T14:20:00',
        budget: 500000000,
        realization: 250000000,
        variance: -250000000,
        issues: 2
      },
      {
        id: 3,
        type: 'TOWER',
        typeIcon: Building2,
        name: 'JKT-South-003',
        code: 'TWR-JKT-003',
        projectId: 1,
        projectName: 'Telkomsel Jakarta Selatan',
        location: 'Jakarta Selatan',
        progress: 25,
        ragStatus: 'RED',
        trend: 'DOWN',
        lastUpdate: '2025-01-16T09:15:00',
        budget: 500000000,
        realization: 150000000,
        variance: -350000000,
        issues: 5
      },
      {
        id: 4,
        type: 'FO',
        typeIcon: Radio,
        name: 'Segment A',
        code: 'SEG-JKT-BDG-A',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        location: 'Jakarta - Bandung',
        progress: 92,
        ragStatus: 'GREEN',
        trend: 'UP',
        lastUpdate: '2025-01-18T16:45:00',
        budget: 800000000,
        realization: 736000000,
        variance: -64000000,
        issues: 0
      },
      {
        id: 5,
        type: 'FO',
        typeIcon: Radio,
        name: 'Segment B',
        code: 'SEG-JKT-BDG-B',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        location: 'Jakarta - Bandung',
        progress: 68,
        ragStatus: 'AMBER',
        trend: 'STABLE',
        lastUpdate: '2025-01-17T11:30:00',
        budget: 800000000,
        realization: 544000000,
        variance: -256000000,
        issues: 1
      },
      {
        id: 6,
        type: 'FO',
        typeIcon: Radio,
        name: 'Segment C',
        code: 'SEG-JKT-BDG-C',
        projectId: 2,
        projectName: 'IOH Fiber Optic Jakarta-Bandung',
        location: 'Jakarta - Bandung',
        progress: 35,
        ragStatus: 'RED',
        trend: 'DOWN',
        lastUpdate: '2025-01-15T08:20:00',
        budget: 800000000,
        realization: 280000000,
        variance: -520000000,
        issues: 3
      },
      {
        id: 7,
        type: 'TOWER',
        typeIcon: Building2,
        name: 'SBY-East-001',
        code: 'TWR-SBY-001',
        projectId: 3,
        projectName: 'XL Smart Tower Expansion',
        location: 'Surabaya Timur',
        progress: 78,
        ragStatus: 'GREEN',
        trend: 'UP',
        lastUpdate: '2025-01-18T13:10:00',
        budget: 450000000,
        realization: 351000000,
        variance: -99000000,
        issues: 0
      },
      {
        id: 8,
        type: 'TOWER',
        typeIcon: Building2,
        name: 'SBY-East-002',
        code: 'TWR-SBY-002',
        projectId: 3,
        projectName: 'XL Smart Tower Expansion',
        location: 'Surabaya Timur',
        progress: 55,
        ragStatus: 'AMBER',
        trend: 'UP',
        lastUpdate: '2025-01-17T15:40:00',
        budget: 450000000,
        realization: 247500000,
        variance: -202500000,
        issues: 1
      },
      {
        id: 9,
        type: 'FO',
        typeIcon: Radio,
        name: 'Palapa-1',
        code: 'SEG-PALAPA-1',
        projectId: 4,
        projectName: 'BAKTI Palapa Ring',
        location: 'Palapa Ring',
        progress: 88,
        ragStatus: 'GREEN',
        trend: 'STABLE',
        lastUpdate: '2025-01-18T12:00:00',
        budget: 1200000000,
        realization: 1056000000,
        variance: -144000000,
        issues: 0
      },
      {
        id: 10,
        type: 'FO',
        typeIcon: Radio,
        name: 'Palapa-2',
        code: 'SEG-PALAPA-2',
        projectId: 4,
        projectName: 'BAKTI Palapa Ring',
        location: 'Palapa Ring',
        progress: 42,
        ragStatus: 'AMBER',
        trend: 'DOWN',
        lastUpdate: '2025-01-16T10:25:00',
        budget: 1200000000,
        realization: 504000000,
        variance: -696000000,
        issues: 2
      }
    ];

    setData(mockData);
    setFilteredData(mockData);
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.projectName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedProject) {
      filtered = filtered.filter(item => item.projectId === parseInt(selectedProject));
    }

    if (selectedType) {
      filtered = filtered.filter(item => item.type === selectedType);
    }

    if (selectedRAGStatus) {
      filtered = filtered.filter(item => item.ragStatus === selectedRAGStatus);
    }

    if (selectedTrend) {
      filtered = filtered.filter(item => item.trend === selectedTrend);
    }

    setFilteredData(filtered);
  };

  const calculateSummary = () => {
    const stats = {
      total: filteredData.length,
      red: filteredData.filter(item => item.ragStatus === 'RED').length,
      amber: filteredData.filter(item => item.ragStatus === 'AMBER').length,
      green: filteredData.filter(item => item.ragStatus === 'GREEN').length,
      trendingUp: filteredData.filter(item => item.trend === 'UP').length,
      trendingDown: filteredData.filter(item => item.trend === 'DOWN').length,
      stable: filteredData.filter(item => item.trend === 'STABLE').length
    };
    setSummaryStats(stats);
  };

  const handleViewProgress = (item) => {
    setSelectedItem(item);
    setShowProgressModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProject('');
    setSelectedType('');
    setSelectedRAGStatus('');
    setSelectedTrend('');
  };

  const getRAGBadge = (status) => {
    const config = {
      RED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: XCircle },
      AMBER: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', icon: AlertCircle },
      GREEN: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: CheckCircle }
    };

    const { bg, text, border, icon: Icon } = config[status] || config.GREEN;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${bg} ${text} ${border}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getTrendIcon = (trend) => {
    const config = {
      UP: { icon: TrendingUp, color: 'text-green-600' },
      DOWN: { icon: TrendingDown, color: 'text-red-600' },
      STABLE: { icon: Minus, color: 'text-gray-600' }
    };

    const { icon: Icon, color } = config[trend] || config.STABLE;
    return <Icon className={`w-4 h-4 ${color}`} />;
  };

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">RAG Status Monitor</h1>
            <p className="text-gray-600 mt-1">Comprehensive project health monitoring dashboard</p>
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Red Card */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">RED</span>
          </div>
          <div className="mb-2">
            <div className="text-3xl font-bold">{summaryStats.red}</div>
            <div className="text-red-100 text-sm">Red Status Items</div>
          </div>
          <div className="text-xs text-red-100 pt-3 border-t border-white border-opacity-20">
            Critical attention required
          </div>
        </div>

        {/* Amber Card */}
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">AMBER</span>
          </div>
          <div className="mb-2">
            <div className="text-3xl font-bold">{summaryStats.amber}</div>
            <div className="text-yellow-100 text-sm">Amber Status Items</div>
          </div>
          <div className="text-xs text-yellow-100 pt-3 border-t border-white border-opacity-20">
            Needs monitoring
          </div>
        </div>

        {/* Green Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">GREEN</span>
          </div>
          <div className="mb-2">
            <div className="text-3xl font-bold">{summaryStats.green}</div>
            <div className="text-green-100 text-sm">Green Status Items</div>
          </div>
          <div className="text-xs text-green-100 pt-3 border-t border-white border-opacity-20">
            On track
          </div>
        </div>

        {/* Total Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="text-xs font-semibold px-2 py-1 bg-white bg-opacity-20 rounded-full">TOTAL</span>
          </div>
          <div className="mb-2">
            <div className="text-3xl font-bold">{summaryStats.total}</div>
            <div className="text-blue-100 text-sm">Total Items</div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs pt-3 border-t border-white border-opacity-20">
            <div className="text-center">
              <div className="font-bold text-lg">{summaryStats.trendingUp}</div>
              <div className="text-blue-100">Up</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">{summaryStats.stable}</div>
              <div className="text-blue-100">Stable</div>
            </div>
            <div className="text-center">
              <div className="font-bold text-lg">{summaryStats.trendingDown}</div>
              <div className="text-blue-100">Down</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Search and Filter Bar */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, code, or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`px-4 py-2 border-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                showFilterPanel 
                  ? 'border-red-600 bg-red-50 text-red-600' 
                  : 'border-gray-300 text-gray-700 hover:border-red-500 hover:bg-red-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedProject || selectedType || selectedRAGStatus || selectedTrend) && (
                <span className="ml-1 px-2 py-0.5 bg-red-600 text-white rounded-full text-xs font-bold">
                  Active
                </span>
              )}
            </button>
            <button 
              onClick={() => window.print()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>

          {/* Filter Panel */}
          {showFilterPanel && (
            <div className="mt-4 p-4 bg-white border-2 border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Project</label>
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Projects</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.projectName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="FO">FO Segment</option>
                    <option value="TOWER">Tower Site</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">RAG Status</label>
                  <select
                    value={selectedRAGStatus}
                    onChange={(e) => setSelectedRAGStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="RED">🔴 Red</option>
                    <option value="AMBER">🟡 Amber</option>
                    <option value="GREEN">🟢 Green</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Trend</label>
                  <select
                    value={selectedTrend}
                    onChange={(e) => setSelectedTrend(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                  >
                    <option value="">All Trends</option>
                    <option value="UP">📈 Improving</option>
                    <option value="STABLE">➡️ Stable</option>
                    <option value="DOWN">📉 Declining</option>
                  </select>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Name / Code</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Project</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Progress</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">RAG Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Trend</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Budget Variance</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Issues</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Last Update</th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No data found</p>
                    <p className="text-gray-400 text-sm">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, index) => (
                  <tr key={`${item.type}-${item.id}-${index}`} className="hover:bg-red-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${
                        item.type === 'FO' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {item.type === 'TOWER' ? (
                          <Building2 className="w-3 h-3 mr-1" />
                        ) : (
                          <Radio className="w-3 h-3 mr-1" />
                        )}
                        {item.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900">{item.name}</div>
                      <div className="text-xs text-gray-500">{item.code}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.projectName}</div>
                      <div className="text-xs text-gray-500">{item.location}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-gray-700">{item.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${getProgressColor(item.progress)}`}
                            style={{ width: `${item.progress}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRAGBadge(item.ragStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTrendIcon(item.trend)}
                        <span className="text-sm text-gray-900">{item.trend}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${
                        item.variance > 0 ? 'text-red-600' : item.variance < 0 ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {formatCurrency(item.variance)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.issues > 0 ? (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-red-100 text-red-800 border border-red-300">
                          {item.issues} open
                        </span>
                      ) : (
                        <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-800 border border-green-300">
                          None
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-900">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(item.lastUpdate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewProgress(item)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Showing <span className="font-bold text-red-600">{filteredData.length}</span> of <span className="font-bold">{data.length}</span> items
            </div>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold border border-red-300">RED</span>
                <span className="text-gray-600">{summaryStats.red}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-bold border border-amber-300">AMBER</span>
                <span className="text-gray-600">{summaryStats.amber}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-bold border border-green-300">GREEN</span>
                <span className="text-gray-600">{summaryStats.green}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Modal */}
      {showProgressModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold">Progress Details</h2>
                  <p className="text-red-100 text-sm mt-1">
                    {selectedItem.type === 'FO' ? 'FO Segment' : 'Tower Site'}: {selectedItem.name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowProgressModal(false);
                    setSelectedItem(null);
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
                  <p className="text-gray-900">{selectedItem.projectName}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                  <p className="text-gray-900">{selectedItem.location}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Progress</label>
                  <div className="w-full">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{selectedItem.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${getProgressColor(selectedItem.progress)}`}
                        style={{ width: `${selectedItem.progress}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">RAG Status</label>
                  {getRAGBadge(selectedItem.ragStatus)}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Trend</label>
                  <div className="flex items-center gap-2">
                    {getTrendIcon(selectedItem.trend)}
                    <span className="text-sm text-gray-900">{selectedItem.trend}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Open Issues</label>
                  <p className="text-gray-900 font-semibold">{selectedItem.issues} issues</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Budget</label>
                  <p className="text-gray-900">{formatCurrency(selectedItem.budget)}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Realization</label>
                  <p className="text-gray-900">{formatCurrency(selectedItem.realization)}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Variance</label>
                  <p className={`font-semibold ${
                    selectedItem.variance > 0 ? 'text-red-600' : selectedItem.variance < 0 ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {formatCurrency(selectedItem.variance)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Update</label>
                  <p className="text-gray-900">{formatDate(selectedItem.lastUpdate)}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
              <button
                onClick={() => {
                  setShowProgressModal(false);
                  setSelectedItem(null);
                }}
                className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RAGStatusList;